import {Box} from '@tempo/core';

// --- v3ga pattern-generator parameters ------------------------------
// https://www.v3ga.net/deezer/pattern-generator/v3/?debug=1
// {"size":[1080,1080], "shape_color":"#963FF6", "ellipses_per_lines":10,
//  "number_lines":0 (→ 1 row), "ellipses_size_x":100, "margin":20,
//  "wave":true, "amplitude":60, "wavelength":10, "direction":0 (←→),
//  "speed":0.25}
const COUNT = 10;
const WAVELENGTH = 10;
const SPEED_HZ = 0.25;
const DURATION_SEC = 1 / SPEED_HZ; // 4s per full cosine period
const AMPLITUDE_PCT = 60;
const KEYFRAME_STEPS = 24;

// The component renders in a 298×172 box (aspect ≈ 1.73). We match the
// viewBox to that aspect so `ry_max = VB_H/2` fits exactly inside the
// container: ellipses never overflow top/bottom (safe zone guaranteed).
const VB_W = 864;
const VB_H = Math.round((VB_W * 172) / 298); // ≈ 499
const ELLIPSE_W = VB_W / COUNT; // 86.4 — size_x = 100% of column width
const RY_MAX = VB_H / 2;
const RATIO_MIN = 1 - AMPLITUDE_PCT / 100; // 0.4

/**
 * Per-column phase for direction=0 ('←→') with even COUNT.
 * Mirrors v3ga's `getWaveEllipseMasterHeight()` source:
 *   i<half: phase = map(i, 0, half-1, 0, f*π)
 *   i≥half: phase = map(i, half, COUNT-1, f*π, 0)  (bEven=true)
 */
const phaseNormalized = (i: number): number => {
	const half = COUNT / 2;
	const f = 100 / WAVELENGTH;
	const phaseRad =
		i < half
			? (i / (half - 1)) * f * Math.PI
			: ((COUNT - 1 - i) / (half - 1)) * f * Math.PI;
	return (((phaseRad / (2 * Math.PI)) % 1) + 1) % 1;
};

/**
 * Cosine keyframes: scaleY(t) = min + (cos(2πt)+1)/2 · (1-min).
 * Sampled every 1/KEYFRAME_STEPS so linear CSS interpolation
 * between stops approximates the continuous cosine smoothly.
 */
const KEYFRAMES_CSS = (() => {
	const stops: string[] = [];
	for (let s = 0; s <= KEYFRAME_STEPS; s++) {
		const t = s / KEYFRAME_STEPS;
		const k =
			RATIO_MIN + ((Math.cos(2 * Math.PI * t) + 1) / 2) * (1 - RATIO_MIN);
		stops.push(`${(t * 100).toFixed(3)}%{transform:scaleY(${k.toFixed(4)})}`);
	}
	return `@keyframes spotaiWaveCos{${stops.join('')}}`;
})();

/**
 * Faithful React port of the Deezer v3ga pattern-generator loader
 * (row of 10 purple ellipses whose vertical size pulses as a traveling
 * cosine wave, symmetric around the centre).
 */
export const ScanningWaveLoader = () => (
	<Box
		role="presentation"
		aria-hidden="true"
		data-testid="ScanningWaveLoader"
		width="150px"
		height="150px"
		overflow="hidden"
		color="background.accent.primary.default"
	>
		<style>{KEYFRAMES_CSS}</style>
		<svg
			width="100%"
			height="100%"
			viewBox={`0 0 ${VB_W} ${VB_H}`}
			preserveAspectRatio="xMidYMid meet"
			aria-hidden="true"
			fill="currentColor"
			style={{display: 'block'}}
		>
			{Array.from({length: COUNT}).map((_, i) => {
				const cx = ELLIPSE_W / 2 + i * ELLIPSE_W;
				const delaySec = -phaseNormalized(i) * DURATION_SEC;
				return (
					<ellipse
						key={i}
						cx={cx}
						cy={VB_H / 2}
						rx={ELLIPSE_W / 2}
						ry={RY_MAX}
						style={{
							transformBox: 'fill-box',
							transformOrigin: 'center',
							animation: `spotaiWaveCos ${DURATION_SEC}s linear ${delaySec}s infinite`,
						}}
					/>
				);
			})}
		</svg>
	</Box>
);
